import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  output,
  signal,
  viewChildren,
} from "@angular/core";
import {
  ImportReport,
  ImportReportEntryType,
  ImportReportStat,
} from "./import-report.model";

@Component({
  selector: "app-import-report",
  templateUrl: "./import-report.component.html",
  styleUrls: ["./import-report.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportReportComponent {
  public readonly report = input<ImportReport>(null);
  readonly closed = output();

  protected readonly activeEntry = signal<number>(null);

  private readonly entryItems =
    viewChildren<ElementRef<HTMLLIElement>>("entryItem");

  private readonly entryPositions = computed(() => {
    const positions: Record<ImportReportEntryType, number[]> = {
      success: [],
      info: [],
      warning: [],
      error: [],
    };
    this.report()?.entries.forEach((entry, index) =>
      positions[entry.type].push(index),
    );

    return positions;
  });

  private cursor = -1;
  private cursorType: ImportReportEntryType = null;

  public entryIcon(type: ImportReportEntryType): string {
    switch (type) {
      case "error":
        return "pi-times-circle";
      case "warning":
        return "pi-exclamation-triangle";
      case "info":
        return "pi-info-circle";
      default:
        return "pi-check-circle";
    }
  }

  public statIcon(stat: ImportReportStat): string {
    return stat.icon || this.entryIcon(stat.type);
  }

  // A counter only takes its color once it stands for something that happened.
  public statModifier(stat: ImportReportStat): string {
    return stat.value > 0 ? `import-report__stat--${stat.type}` : "";
  }

  public hasEntries(type: ImportReportEntryType): boolean {
    return this.entryPositions()[type].length > 0;
  }

  public scrollToEntry(type: ImportReportEntryType, step: number) {
    const positions = this.entryPositions()[type];
    if (!positions.length) {
      return;
    }

    if (this.cursorType !== type) {
      this.cursorType = type;
      this.cursor = -1;
    }

    this.cursor =
      this.cursor < 0 && step < 0
        ? positions.length - 1
        : (this.cursor + step + positions.length) % positions.length;

    const index = positions[this.cursor];
    this.activeEntry.set(index);
    this.entryItems()[index]?.nativeElement.scrollIntoView({
      block: "center",
    });
  }
}
